import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useLang } from "@/i18n";
import { Bell } from "lucide-react";

export default function NotificationBell() {
  const { t } = useLang();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/client/notifications").then((r) => setNotifs(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const openPanel = async () => {
    setOpen(!open);
    if (!open && unread > 0) {
      for (const n of notifs.filter((x) => !x.read)) {
        try { await api.post(`/client/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
      }
      load();
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={openPanel}
        className="relative w-9 h-9 rounded-full border border-[#E6EBF1] bg-white flex items-center justify-center text-[#425466] hover:text-[#635BFF] hover:border-[#635BFF]/40 transition-colors"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span data-testid="notification-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5996] text-white text-[10px] font-bold flex items-center justify-center animate-pulse-dot">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel"
             className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-[#E6EBF1] rounded-2xl shadow-2xl shadow-[#0A2540]/15 z-50 animate-fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A2540] px-4 pt-4 pb-2 border-b border-[#E6EBF1]">
            {t("notif.title")}
          </p>
          {notifs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="notification-empty">{t("notif.empty")}</p>
          ) : (
            notifs.map((n) => (
              <div key={n.id} data-testid={`notification-item-${n.id}`}
                   className={`px-4 py-3 border-b border-[#E6EBF1] last:border-0 ${!n.read ? "bg-[#635BFF]/5" : ""}`}>
                <p className="text-sm font-semibold text-[#0A2540]">{n.title}</p>
                <p className="text-xs text-[#425466] mt-1 leading-relaxed whitespace-pre-line">{n.body}</p>
                <p className="text-[10px] font-ticker text-slate-400 mt-1.5">{fmtDate(n.created_at)}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
